/**
 * Home / Portal Demo — oRPC procedure resolver.
 *
 * THROWAWAY DEMO CODE. Serves the /inbox "Tasks" tab (tasks + comments) from the
 * resettable tasks store and resolves the synthetic agent the materialize item
 * links to. Everything else falls through to the real backend (and the other
 * demo resolvers). Delete with src/demo/home.
 */

import type { TaskResponse } from '@erp/rpc';
import type { DemoProcedureResolver } from '@/demo/core/demo-client';
import { HOME_DEMO_AGENT, HOME_DEMO_AGENT_ID } from './fixtures';
import { homeInboxUnreadCount } from './inbox-store';
import {
  assignHomeTaskToAgent,
  createHomeTaskComment,
  listHomeTaskComments,
  listHomeTasks,
  returnHomeTasksToAgent,
  updateHomeTaskStatus,
} from './tasks-store';

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : [];
}

export const homeDemoResolver: DemoProcedureResolver = (path, input) => {
  const key = path.join('.');
  const inp = (input ?? {}) as Record<string, unknown>;

  switch (key) {
    case 'tasks.list':
      return listHomeTasks(
        typeof inp.search === 'string' ? inp.search : undefined,
      );

    case 'tasks.assign':
      return assignHomeTaskToAgent(
        String(inp.id ?? ''),
        String(inp.assigneeAgentDefinitionId ?? ''),
      );

    case 'tasks.batchUpdate':
      return updateHomeTaskStatus(
        asStringArray(inp.taskIds),
        inp.status as TaskResponse['status'],
      );

    case 'tasks.batchReturnToAgent':
      return returnHomeTasksToAgent(asStringArray(inp.taskIds));

    // The demo task has no screen recording — short-circuit so the detail pane
    // doesn't 404 against the real backend.
    case 'tasks.getRecording':
      return null;

    case 'comments.list':
      return inp.commentableType === 'TASK'
        ? listHomeTaskComments(String(inp.commentableId ?? ''))
        : undefined;

    case 'comments.create':
      return inp.commentableType === 'TASK'
        ? createHomeTaskComment(
            String(inp.commentableId ?? ''),
            String(inp.body ?? ''),
          )
        : undefined;

    // Only the synthetic materialize agent — real agent ids fall through so the
    // agents list / monitor keep their real data in demo mode.
    case 'agentDefinitions.get':
      return inp.agentDefinitionId === HOME_DEMO_AGENT_ID
        ? HOME_DEMO_AGENT
        : undefined;

    // The demo inbox is served from the home inbox store (merged into the inbox
    // view directly), so suppress the real notification + approval feeds and
    // report the demo's own unread count for the badge.
    case 'communications.notifications.list':
      return { notifications: [], nextCursor: null };
    case 'communications.notifications.unreadCount':
      return { count: homeInboxUnreadCount() };
    case 'workflows.listPendingApprovals':
      return { approvals: [] };

    default:
      return undefined;
  }
};
